import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  FolderOpen,
  CreditCard,
  Settings,
  Shield,
  FileText,
  Users,
  ShieldCheck,
} from 'lucide-react'
import { BarChart3 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { useBranding } from '@/hooks/useBranding'
import { usePlanLimits } from '@/hooks/usePlanLimits'

interface NavItem {
  label: string
  href: string
  icon: React.ElementType
  hideForCustomPricing?: boolean
  showForCustomPricing?: boolean
  /** If set, only users with one of these org roles see this item. */
  roles?: string[]
}

const adminNavItems: NavItem[] = [
  { label: 'Admin Dashboard', href: '/admin', icon: Shield },
  { label: 'Organizations', href: '/admin/organizations', icon: FolderOpen },
  { label: 'Users', href: '/admin/users', icon: Users },
  { label: 'Billing Plans', href: '/admin/billing-plans', icon: FileText },
  { label: 'Auth Logs', href: '/admin/auth-logs', icon: ShieldCheck },
]

function isNavActive(pathname: string, href: string): boolean {
  if (pathname === href) return true
  // Only match prefix if the next char is '/' (prevents /settings matching /settings/org)
  if (pathname.startsWith(href + '/')) return true
  return false
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Projects', href: '/projects', icon: FolderOpen },
  { label: 'Org Settings', href: '/settings/org', icon: Settings, roles: ['org_owner', 'org_admin'] },
  { label: 'Members', href: '/settings/members', icon: Users, roles: ['org_owner', 'org_admin'] },
  { label: 'Usage', href: '/usage', icon: BarChart3, showForCustomPricing: true, roles: ['org_owner', 'org_admin', 'org_billing'] },
  { label: 'Billing', href: '/billing', icon: CreditCard, hideForCustomPricing: true, roles: ['org_owner', 'org_billing'] },
]

export function Sidebar() {
  const location = useLocation()
  const { user, activeOrgRole } = useAuth()
  const { brandName, brandLogoUrl, isWhitelabel } = useBranding()
  const { isCustomPricing } = usePlanLimits()

  return (
    <aside className="flex h-screen w-64 flex-col border-r bg-background">
      {/* Logo */}
      <div className="flex h-16 items-center border-b px-4">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src={brandLogoUrl} alt={brandName} className="h-6" />
          {!isWhitelabel && (
            <span className="text-sm font-semibold text-muted-foreground">|</span>
          )}
          <span className="text-base font-bold">{brandName}</span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="px-3">
          <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Main
          </p>
          <ul className="space-y-1">
            {navItems
              .filter((item) => {
                if (isCustomPricing && item.hideForCustomPricing) return false
                if (!isCustomPricing && item.showForCustomPricing) return false
                // Superadmins see everything (so they can inspect any tab)
                if (item.roles && !user?.is_superadmin && !item.roles.includes(activeOrgRole || '')) return false
                return true
              })
              .map((item) => {
              const Icon = item.icon
              const isActive = isNavActive(location.pathname, item.href)
              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                </li>
              )
            })}
          </ul>

          {user?.is_superadmin && (
            <div className="mt-6">
              <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Admin
              </p>
              <ul className="space-y-1">
                {adminNavItems.map((item) => {
                  const Icon = item.icon
                  const isActive = isNavActive(location.pathname, item.href)
                  return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>
      </nav>
    </aside>
  )
}
